import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import jp from 'jsonpath';
import { set } from 'lodash-es';
import { useIntl } from '@leagueplatform/locales';
import {
  useAddConfigMessage,
  useConfigUserFeedback,
  useLocales,
  useSetEntityData,
} from '@web-config-app/core-react';
import { useEntityDetailsProps } from '../use-entity-details-props/use-entity-details-props';

/**
 * This hook is used to manage the selection of items in an array when moving items between arrays
 * @param items - The array of items to select from
 * @param getItemLabel - A function to get the label of an item
 * @param getItemData - A function to get the data of an item
 * @returns An object with the selection state and methods to handle the selection
 */
export const useMoveArrayItems = <T extends object>({
  items,
  getItemLabel,
  getItemData,
  localized,
}: {
  items: T[];
  getItemLabel: (item: T) => string;
  getItemData: (item: T) => any;
  localized?: boolean;
}) => {
  const {
    formPath: currentPath,
    entityRootData,
    entity,
  } = useEntityDetailsProps();
  const { locales } = useLocales();
  const nonDefaultLocales = locales?.filter((locale) => !locale.isDefault);
  const isLocalizable = localized && nonDefaultLocales && entity.localizable;

  const { setConfigError, setShowErrorBanner } = useConfigUserFeedback(
    (state) => ({
      setConfigError: state.setConfigError,
      setShowErrorBanner: state.setShowErrorBanner,
    }),
  );
  const { formatMessage } = useIntl();
  const addConfigMessage = useAddConfigMessage();
  const navigate = useNavigate();
  const setEntityData = useSetEntityData();

  const [isSelectionMode, setIsSelectionMode] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState<Map<number, string>>(
    new Map(),
  );
  const [isMoveItemsModalOpen, setIsMoveItemsModalOpen] = React.useState(false);
  /**
   * The array of items to move from one location to another. Contains the itemData and itemLabel, among other properties to be used when moving items.
   */
  const [selectedMoveItems, setSelectedMoveItems] = React.useState<T[]>([]);

  const handleCancelMove = () => {
    setIsSelectionMode(false);
    setSelectedItems(new Map());
  };

  React.useEffect(() => {
    if (currentPath) {
      handleCancelMove();
    }
  }, [currentPath]);

  const handleToggleSelection = (index: number, itemLabel: string) => {
    setSelectedItems((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(index)) {
        newMap.delete(index);
      } else {
        newMap.set(index, itemLabel);
      }
      return newMap;
    });
  };

  const handleSelectAll = () => {
    const allItemLabels = items.map(getItemLabel);
    const isAllSelected =
      allItemLabels.length > 0 &&
      allItemLabels.every((_, index) => selectedItems.has(index));

    if (isAllSelected) {
      // If all are selected, deselect all
      setSelectedItems(new Map());
    } else {
      // If not all are selected, select all
      const newMap = new Map();
      allItemLabels.forEach((label, index) => {
        newMap.set(index, label);
      });
      setSelectedItems(newMap);
    }
  };

  const handleMoveClick = () => {
    setIsSelectionMode(true);
  };

  const handleSelectDestination = () => {
    const selectedItemObjects = items.filter((_, index) =>
      selectedItems.has(index),
    );
    setIsMoveItemsModalOpen(true);
    setSelectedMoveItems(selectedItemObjects);
  };

  /**
   * Helper function to update non-default locale arrays during move operations
   * @param data - The data object containing all entity data
   * @param sourcePath - The path to the source array
   * @param targetPath - The path to the target array
   * @param selectedIndices - Indices of items to move
   * @param selectedItemsLabels - Labels of the items being moved, used for error messages
   */
  const updateLocalizedArrays = useCallback(
    (
      data: any,
      sourcePath: string,
      targetPath: string,
      selectedIndices: number[],
      selectedItemsLabels: string[] = [],
    ) => {
      // Get the source array from the default locale for reference
      const defaultSourceArray =
        jp.value(data?.attributes?.domainEntityAttributes, sourcePath) || [];

      // Apply the same operation for each non-default locale
      nonDefaultLocales?.forEach((locale) => {
        try {
          // Ensure the translations structure exists
          if (!data?.attributes?.translations?.[locale.code]) {
            set(data, `attributes.translations.${locale.code}`, {
              domainEntityAttributes: {},
            });
          }

          // Get the locale's source array
          let localeSourceArray = jp.value(
            data?.attributes?.translations?.[locale.code]
              ?.domainEntityAttributes,
            sourcePath,
          );

          // If no source array exists, create a placeholder array matching default locale length
          if (!localeSourceArray || !Array.isArray(localeSourceArray)) {
            localeSourceArray = Array(defaultSourceArray.length).fill({});
          }

          // Get the locale's target array
          let localeTargetArray = jp.value(
            data?.attributes?.translations?.[locale.code]
              ?.domainEntityAttributes,
            targetPath,
          );

          // If no target array exists, create an empty array
          if (!localeTargetArray || !Array.isArray(localeTargetArray)) {
            localeTargetArray = [];
          }

          // Extract items to move from source array, using placeholders for missing items
          const itemsToMove = selectedIndices.map((index) => {
            const item = localeSourceArray[index];

            // If the item doesn't exist or is null in this locale, use an empty placeholder object
            return item !== undefined && item !== null ? item : {};
          });

          // Add items to target array
          const updatedLocaleTargetArray = [
            ...itemsToMove,
            ...localeTargetArray,
          ];
          // Remove items from source array
          const updatedLocaleSourceArray = localeSourceArray.filter(
            (_: any, index: number) => !selectedIndices.includes(index),
          );

          // Update both arrays in the locale data
          set(
            data?.attributes?.translations?.[locale.code]
              ?.domainEntityAttributes,
            targetPath,
            updatedLocaleTargetArray,
          );

          set(
            data?.attributes?.translations?.[locale.code]
              ?.domainEntityAttributes,
            sourcePath,
            updatedLocaleSourceArray,
          );
        } catch (error) {
          setIsMoveItemsModalOpen(false);
          setConfigError({
            type: null,
            errors: [
              {
                detail: formatMessage(
                  { id: 'MOVING_ITEMS_ERROR' },
                  { items: selectedItemsLabels.join(', ') },
                ),
              },
            ],
          });
          setShowErrorBanner(true);
        }
      });
    },
    [formatMessage, nonDefaultLocales, setConfigError, setShowErrorBanner],
  );

  const handleMoveItems = useCallback(
    (targetPath: string) => {
      const selectedItemsLabels = Array.from(selectedItems.values());
      try {
        const newData = JSON.parse(JSON.stringify(entityRootData));

        // Get items to move and their indices
        const selectedItemsToMove = selectedMoveItems.map((item) =>
          getItemData(item),
        );
        const selectedItemsIndices = Array.from(selectedItems.keys()).sort();

        // Get the target array from the entityRootData
        const targetArray = jp.value(
          newData?.attributes?.domainEntityAttributes,
          targetPath,
        );

        if (!Array.isArray(targetArray)) {
          setIsMoveItemsModalOpen(false);
          setConfigError({
            type: null,
            errors: [
              {
                detail: formatMessage(
                  { id: 'MOVING_ITEMS_ERROR' },
                  { items: selectedItemsLabels.join(', ') },
                ),
              },
            ],
          });
          setShowErrorBanner(true);
          return;
        }

        // Add the selected items to the target array (for default locale)
        const updatedArray = [...selectedItemsToMove, ...targetArray];

        // Update the data at the target path for default locale
        set(
          newData?.attributes?.domainEntityAttributes,
          targetPath,
          updatedArray,
        );

        if (currentPath) {
          const currentArray = jp.value(
            newData?.attributes?.domainEntityAttributes,
            currentPath,
          );

          // Remove selected items from source array
          const filteredArray = currentArray.filter(
            (_: any, index: number) => !selectedItemsIndices.includes(index),
          );

          // Update the data at the current path by removing the selected items
          set(
            newData?.attributes?.domainEntityAttributes,
            currentPath,
            filteredArray,
          );
        }

        // Handle localized array items if needed
        if (currentPath && isLocalizable) {
          // Update all non-default locale arrays
          updateLocalizedArrays(
            newData,
            currentPath,
            targetPath,
            selectedItemsIndices,
            selectedItemsLabels,
          );
        }

        // Update the entity data once with all changes (default locale and translations)
        setEntityData(newData);

        // Close the modal
        setIsMoveItemsModalOpen(false);

        // Navigate to the target path
        navigate(`?path=${targetPath}`);
        addConfigMessage({
          type: 'success',
          text: formatMessage(
            { id: 'MOVING_ITEMS_SUCCESS' },
            { items: selectedItemsLabels.join(', ') },
          ),
        });
      } catch (error) {
        setIsMoveItemsModalOpen(false);
        setConfigError({
          type: null,
          errors: [
            {
              detail: formatMessage(
                { id: 'MOVING_ITEMS_ERROR' },
                { items: selectedItemsLabels.join(', ') },
              ),
            },
          ],
        });
        setShowErrorBanner(true);
      }
    },
    [
      entityRootData,
      currentPath,
      setEntityData,
      setIsMoveItemsModalOpen,
      navigate,
      getItemData,
      selectedMoveItems,
      selectedItems,
      setConfigError,
      addConfigMessage,
      setShowErrorBanner,
      formatMessage,
      isLocalizable,
      updateLocalizedArrays,
    ],
  );

  return {
    isSelectionMode,
    setIsSelectionMode,
    selectedItems,
    setSelectedItems,
    handleToggleSelection,
    handleMoveClick,
    handleCancelMove,
    handleSelectDestination,
    handleSelectAll,
    isMoveItemsModalOpen,
    setIsMoveItemsModalOpen,
    selectedMoveItems,
    setSelectedMoveItems,
    handleMoveItems,
  };
};
